import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { dataSource } from '@/services/datasource'
import type { UserObjType } from '@/types/bluelink'

export type AuthVerifyStatus = 'unknown' | 'verifying' | 'authenticated' | 'unauthenticated'

const STORAGE_KEY = 'access-token'
const EXPIRY_KEY = 'access-token-expiry'
const USER_KEY = 'access-token-user'
const VERIFY_TIMEOUT_MS = 8000

function isExpiredDate(expiredate: string | number): boolean {
  let ts: number
  if (typeof expiredate === 'number') {
    ts = expiredate
  } else {
    // Numeric strings (e.g. '1700000000') are how setSession persists number expiries.
    // new Date('1700000000') returns Invalid Date in V8, so try Number() first.
    const asNum = Number(expiredate)
    ts = Number.isFinite(asNum) ? asNum : new Date(expiredate).getTime()
  }
  // Handle Unix seconds (< year 3000 in ms) vs milliseconds
  const tsMs = ts < 1e12 ? ts * 1000 : ts
  return Date.now() > tsMs
}

// Clear stored token immediately on boot if it has expired
const storedExpiry = localStorage.getItem(EXPIRY_KEY)
const tokenIsExpired = storedExpiry ? isExpiredDate(storedExpiry) : false
if (tokenIsExpired) {
  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(EXPIRY_KEY)
  localStorage.removeItem(USER_KEY)
}

// Hydrate user from storage when token is present and not expired
const storedUser = ((): UserObjType | null => {
  if (tokenIsExpired) return null
  const raw = localStorage.getItem(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as UserObjType
  } catch {
    return null
  }
})()

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(
    tokenIsExpired ? null : localStorage.getItem(STORAGE_KEY),
  )
  const user = ref<UserObjType | null>(storedUser)
  const loginStatus = ref<'idle' | 'connecting' | 'authenticating'>('idle')
  const verifyStatus = ref<AuthVerifyStatus>(
    accessToken.value ? (user.value ? 'authenticated' : 'verifying') : 'unauthenticated',
  )

  let _verifyPromise: Promise<void> | null = null

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)

  const isExpired = computed(() => {
    if (user.value?.expire == null) return false
    return isExpiredDate(user.value.expire)
  })

  function setSession(token: string, userData: UserObjType): void {
    accessToken.value = token
    user.value = userData
    localStorage.setItem(STORAGE_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
    if (userData.expire != null) {
      localStorage.setItem(EXPIRY_KEY, String(userData.expire))
    }
    verifyStatus.value = 'authenticated'
  }

  function clearSession(): void {
    accessToken.value = null
    user.value = null
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(EXPIRY_KEY)
    localStorage.removeItem(USER_KEY)
    verifyStatus.value = 'unauthenticated'
  }

  async function verifyToken(): Promise<void> {
    verifyStatus.value = 'verifying'
    try {
      const loginUser = await Promise.race([
        dataSource.reauthWithToken(accessToken.value!),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('auth verification timeout')), VERIFY_TIMEOUT_MS),
        ),
      ])
      if (loginUser) {
        setSession(accessToken.value!, loginUser)
      } else {
        clearSession()
        const { default: router } = await import('@/router')
        if (router.currentRoute.value.name !== 'Login') {
          void router.push('/login')
        }
      }
    } catch {
      // Transient failure (network error, timeout, socket disconnect) — do NOT wipe the session.
      // A genuinely revoked token will produce 401s on real API calls and apiClient handles those.
      _verifyPromise = null
      if (user.value && !isExpired.value) {
        // User was hydrated from storage and is not expired — stay authenticated optimistically.
        verifyStatus.value = 'authenticated'
      } else {
        // No hydrated user yet — leave token intact but mark as retryable.
        verifyStatus.value = 'unknown'
      }
    }
  }

  function ensureVerified(): Promise<void> {
    if (_verifyPromise) return _verifyPromise
    if (verifyStatus.value === 'authenticated' || verifyStatus.value === 'unauthenticated') {
      return Promise.resolve()
    }
    _verifyPromise = verifyToken()
    return _verifyPromise
  }

  function awaitVerification(): Promise<void> {
    return ensureVerified()
  }

  async function loginWithPassword(mobile: string, password: string): Promise<void> {
    loginStatus.value = 'connecting'
    try {
      dataSource.connect()
      loginStatus.value = 'authenticating'
      const { accessToken: token, user: loginUser } = await dataSource.loginWithPassword(mobile, password)
      setSession(token, loginUser)
    } finally {
      loginStatus.value = 'idle'
    }
  }

  async function logout(): Promise<void> {
    clearSession()
    _verifyPromise = null
    dataSource.disconnect()
    const { default: router } = await import('@/router')
    void router.push('/login')
  }

  // Boot: start verification if a token is present
  if (accessToken.value) {
    if (user.value) {
      // User hydrated from storage — render immediately, revalidate in background
      setTimeout(() => {
        if (!_verifyPromise) _verifyPromise = verifyToken()
      }, 0)
    } else {
      void ensureVerified()
    }
  }

  return {
    accessToken,
    user,
    isAuthenticated,
    isExpired,
    loginStatus,
    verifyStatus,
    awaitVerification,
    ensureVerified,
    setSession,
    clearSession,
    loginWithPassword,
    logout,
  }
})
